import styled from "@emotion/styled";
import { format } from "date-fns";
import { ReactNode, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { DesignToken, Heading, Text, Tooltip } from "@livechat/design-system-react-components";

import LinkStyledButton from "components/button/LinkStyledButton";
import { ExternalLink } from "components/external-link";
import Block from "components/layout/Block";
import { SettingsTableColumn as Column, SettingsTableRow as Row, SettingsTable } from "components/settings-table";
import { HelpCenter } from "constants/help-center";
import Role from "constants/role";
import { SettingsSection } from "constants/settings/sections";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import useSettingsSection from "hooks/settings/use-settings-section";
import { IStoreState } from "interfaces/storeState";
import EditModal from "routes/settings/customViews/EditModal";
import CustomViewFilters from "routes/tickets/AddCustomViewModal/CustomViewFilters";
import {
  IAgent,
  selectAgentsAlphabetically,
  selectLoggedInAgent,
  selectLoggedInAgentPermission,
} from "store/entities/agents";
import { selectLanguageLabels } from "store/entities/application-settings/selectors";
import { CustomViewAuthor } from "store/entities/custom-views/interfaces";
import { selectCustomViews, selectCustomViewsAlphabetically } from "store/entities/custom-views/selectors";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { SettingsViewActions } from "store/views/settings";
import { getCustomViewFiltersForDisplay } from "store/views/tickets/helpers";
import { withVar } from "themes/helpers";

import SettingsViewContainer from "../ViewContainer";
import { ContentWrapper, PageWrapper, SettingsActionButtonsWrapper } from "../styled";

import DeleteModal from "./DeleteModal";

const HoverableText = styled(Text)`
  display: inline;
  color: var(${DesignToken.ActionPrimaryDefault});

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: IStoreState) => ({
  customViews: selectCustomViews(state),
  customViewsAlphabetically: selectCustomViewsAlphabetically(state),
  agents: selectAgentsAlphabetically(state),
  currentAgent: selectLoggedInAgent(state),
  teams: selectTeamsAlphabetically(state),
  tags: selectTagsAlphabetically(state),
  isAdmin: selectLoggedInAgentPermission(state) === Role.Owner,
  languageLabels: selectLanguageLabels(state),
});

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: Dispatch) => ({
  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(2345)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  goToCustomView: (id: string) => dispatch(SettingsViewActions.goToCustomView({ id })),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCustomView: (id: string, name: string, shared?: boolean) =>
    // @ts-expect-error TS(2345)
    dispatch(SettingsViewActions.updateCustomView({ id, name, shared })),
  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(2345)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteCustomView: (id: string) => dispatch(SettingsViewActions.deleteCustomView({ id })),
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;

const getAuthorName = (currentAgent: IAgent, agents: IAgent[], authorID: string): ReactNode => {
  const isMe = currentAgent.id === authorID;
  const author = agents.find(agent => agent.id === authorID);
  const authorName = author ? (
    author.name
  ) : (
    <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>(Deleted admin)</Text>
  );

  return isMe ? "Me" : authorName;
};

const CustomViewsUnconnected = ({
  customViews,
  customViewsAlphabetically,
  agents,
  currentAgent,
  teams,
  tags,
  goToCustomView,
  updateCustomView,
  deleteCustomView,
  isAdmin,
  languageLabels,
}: IWithBreakpointsProps & StateProps & DispatchProps): ReactNode => {
  const [toDeleteId, setToDeleteId] = useState<string | null>(null);
  const [toUpdateId, setToUpdateId] = useState<string | null>(null);
  useSettingsSection(SettingsSection.MyViews);

  useEffect(() => {
    trackEngagement("Settings: My views section opened", EventPlace.Settings);
  }, []);

  return (
    <>
      <SettingsViewContainer title="Ticket views">
        <PageWrapper>
          <ContentWrapper>
            <Block marginBottom="25px">
              <Text>
                Filter out the tickets you need and save them as a view. Access your views using the ticket navigation
                bar. As an admin, you can share your views and make them visible to your teammates.{" "}
                <ExternalLink
                  target="_blank"
                  href={HelpCenter.WHAT_ARE_MY_VIEWS}
                  onClick={() => trackEngagement("My views: Learn more clicked", EventPlace.Settings)}
                >
                  Learn more
                </ExternalLink>
              </Text>
            </Block>
            <Heading size="xs" as="h2">
              Views
            </Heading>
            <Text>Go to the main ticket dashboard and filter your tickets to save them as a view.</Text>
            {customViewsAlphabetically.length > 0 && (
              <SettingsTable
                columns={5}
                columnSizes="2fr 2fr 1fr 1fr 1fr"
                headers={["Name", "Filters", "Created", "Created by", "Actions"]}
              >
                {customViewsAlphabetically.map(customView => {
                  const shouldHideTooltip = isAdmin || customView.agentID !== null;

                  const activeFiltersForDisplay = getCustomViewFiltersForDisplay(customView.filters, {
                    agents,
                    tags,
                    teams,
                    languageLabels,
                  });

                  return (
                    <Row key={customView.id}>
                      <Column>
                        <NameField>
                          <HoverableText onClick={() => goToCustomView(customView.id)}>{customView.name}</HoverableText>
                          {customView.agentID === null && (
                            <Text customColor={withVar(DesignToken.ContentBasicSecondary)} css={{ display: "inline" }}>
                              {" "}
                              (Shared)
                            </Text>
                          )}
                        </NameField>
                      </Column>
                      <Column>
                        <CustomViewFilters filtersForDisplay={activeFiltersForDisplay} />
                      </Column>
                      <Column>{format(customView.createdAt, "DD MMM, YYYY")}</Column>
                      <Column>
                        {customView.createdByType === CustomViewAuthor.System
                          ? "HelpDesk"
                          : getAuthorName(currentAgent, agents, customView.createdBy)}
                      </Column>
                      <Column>
                        <SettingsActionButtonsWrapper>
                          <Tooltip
                            isVisible={shouldHideTooltip ? false : undefined}
                            kind="invert"
                            placement="bottom-end"
                            triggerRenderer={
                              <span>
                                <Block marginRight="20px">
                                  <LinkStyledButton
                                    disabled={!isAdmin && customView.agentID === null}
                                    onClick={() => setToUpdateId(customView.id)}
                                  >
                                    Edit
                                  </LinkStyledButton>
                                </Block>
                              </span>
                            }
                          >
                            This is a shared view created by an Admin. You cannot edit or delete it.
                          </Tooltip>
                          <Tooltip
                            isVisible={shouldHideTooltip ? false : undefined}
                            kind="invert"
                            placement="bottom-end"
                            triggerRenderer={
                              <span>
                                {" "}
                                <LinkStyledButton
                                  color="warningColor"
                                  disabled={!isAdmin && customView.agentID === null}
                                  onClick={() => setToDeleteId(customView.id)}
                                >
                                  Delete
                                </LinkStyledButton>
                              </span>
                            }
                          >
                            This is a shared view created by an Admin. You cannot edit or delete it.
                          </Tooltip>
                        </SettingsActionButtonsWrapper>
                      </Column>
                    </Row>
                  );
                })}
              </SettingsTable>
            )}
          </ContentWrapper>
        </PageWrapper>
      </SettingsViewContainer>
      {toUpdateId && (
        <EditModal
          name={customViews.data[toUpdateId].name}
          shared={customViews.data[toUpdateId].agentID === null}
          isAdmin={isAdmin}
          onSave={(name: string, shared?: boolean) => {
            updateCustomView(toUpdateId, name, shared);
            setToUpdateId(null);
          }}
          onCancel={() => setToUpdateId(null)}
        />
      )}
      {toDeleteId && (
        <DeleteModal
          name={customViews.data[toDeleteId].name}
          onConfirm={() => {
            deleteCustomView(toDeleteId);
            setToDeleteId(null);
          }}
          onCancel={() => setToDeleteId(null)}
        />
      )}
    </>
  );
};

const NameField = styled.div`
  display: inline;
`;

const CustomViews = withBreakpoints(connect(mapStateToProps, mapDispatchToProps)(CustomViewsUnconnected));

export default CustomViews;
